import React, { useCallback, useEffect, useState } from 'react';
import {
    Table,
    Typography,
    Input,
    Space,
    Button,
    message,
    Tooltip
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

import {
    NutrigenUpcLink,
    NutrigenUpcLinkQuery,
    AddNutrigenUpcLinkDto,
    UpdateNutrigenUpcLinkDto
} from '../types/nutrigentypes';

import {
    getPagedNutrigenUpcLinks,
    addNutrigenUpcLink,
    updateNutrigenUpcLink
} from '../services/nutrigenservice';
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import NutrigenUpcLinkDrawer from '../components/nutrigen/nutrigenupclinkdrawer';

const { Search } = Input;

const NutrigenUpcLinks: React.FC = () => {
    const [data, setData] = useState<NutrigenUpcLink[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchUpc, setSearchUpc] = useState('');

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] =
        useState<NutrigenUpcLink | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const query: NutrigenUpcLinkQuery = {
                pageNumber,
                pageSize,
                ...(searchUpc && { searchUpc })
            };

            const result = await getPagedNutrigenUpcLinks(query);
            setData(result.data);
            setTotalCount(result.totalCount);
        } catch {
            message.error('Failed to load Nutrigen UPC links.');
        } finally {
            setLoading(false);
        }
    }, [pageNumber, pageSize, searchUpc]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setPageNumber(pagination.current || 1);
        setPageSize(pagination.pageSize || 10);
    };

    const openAddDrawer = () => {
        setSelectedRecord(null);
        setDrawerOpen(true);
    };

    const openEditDrawer = (record: NutrigenUpcLink) => {
        setSelectedRecord(record);
        setDrawerOpen(true);
    };

    const handleAdd = async (dto: AddNutrigenUpcLinkDto) => {
        try {
            setDrawerLoading(true);
            await addNutrigenUpcLink(dto);
            message.success('Nutrigen UPC link added successfully.');
            setDrawerOpen(false);
            fetchData();
        } catch (err: any) {
            message.error(
                err?.response?.data?.message ||
                err?.response?.data ||
                'Failed to add Nutrigen UPC link.'
            );
        } finally {
            setDrawerLoading(false);
        }
    };

    const handleUpdate = async (
        upc: string,
        dto: UpdateNutrigenUpcLinkDto
    ) => {
        try {
            setDrawerLoading(true);
            await updateNutrigenUpcLink(upc, dto);
            message.success('Nutrigen UPC link updated successfully.');
            setDrawerOpen(false);
            fetchData();
        } catch (err: any) {
            message.error(
                err?.response?.data?.message ||
                err?.response?.data ||
                'Failed to update Nutrigen UPC link.'
            );
        } finally {
            setDrawerLoading(false);
        }
    };


    const columns: ColumnsType<NutrigenUpcLink> = [
        {
            title: 'UPC',
            dataIndex: 'upc',
            key: 'upc'
        },
        {
            title: 'Nutrigen Item Number',
            dataIndex: 'nutrigenItenNum',
            key: 'nutrigenItenNum'
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Tooltip title="Edit">
                    <Button
                        icon={
                            <EditOutlined
                                style={{ color: "var(--color-primary)" }}
                            />
                        }
                        onClick={() => openEditDrawer(record)}
                    />
                </Tooltip>
            )
        }
    ];

    return (
        <>
            <Typography.Title>Nutrigen UPC Links</Typography.Title>

            <Space style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Search by UPC"
                    allowClear
                    style={{ width: 300 }}
                    onSearch={(value) => {
                        setSearchUpc(value);
                        setPageNumber(1);
                    }}
                />

                <Button
                    color="primary"
                    variant="filled"
                    icon={<PlusOutlined />}
                    onClick={openAddDrawer}
                >
                    Add Link
                </Button>
            </Space>

            <Table
                rowKey="guid"
                loading={loading}
                columns={columns}
                dataSource={data}
                pagination={{
                    current: pageNumber,
                    pageSize,
                    total: totalCount,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                        `${range[0]}–${range[1]} of ${total} items`
                }}
                onChange={handleTableChange}
            />

            <NutrigenUpcLinkDrawer
                open={drawerOpen}
                loading={drawerLoading}
                record={selectedRecord}
                onClose={() => setDrawerOpen(false)}
                onAdd={handleAdd}
                onUpdate={handleUpdate}
            />
        </>
    );
};

export default NutrigenUpcLinks;